import { useCallback, useEffect, useRef, type RefObject } from 'react';
import { audioKey, type Track } from '../lib/catalog';
import { getClientSessionId } from '../lib/clientSession';

type PlaybackEventDetail = Record<string, unknown>;
type QueuedEvent = { event: string; at: number; detail: PlaybackEventDetail };
export type PlaybackLogger = (event: string, detail?: PlaybackEventDetail) => void;

const logEndpoint = '/api/log';
const flushIntervalMs = 10_000;
const maximumQueuedEvents = 60;
const stallCheckIntervalMs = 2_000;
const stallTriggerSeconds = 2.5;
const stallLogThrottleMs = 5_000;
const immediateFlushEvents = new Set(['session', 'error', 'stall', 'play-rejected', 'audio-graph-error']);
const mediaEventNames = [
  'loadstart',
  'loadedmetadata',
  'loadeddata',
  'canplay',
  'canplaythrough',
  'play',
  'playing',
  'pause',
  'waiting',
  'stalled',
  'suspend',
  'abort',
  'emptied',
  'ended',
  'error',
  'seeking',
  'seeked',
  'ratechange',
] as const;

type NetworkInformation = {
  effectiveType?: string;
  type?: string;
  downlink?: number;
  rtt?: number;
  saveData?: boolean;
};

function roundSeconds(value: number): number {
  return Number.isFinite(value) ? Number(value.toFixed(2)) : 0;
}

function describeConnection(): NetworkInformation | null {
  const connection = (navigator as Navigator & { connection?: NetworkInformation }).connection;
  if (!connection) return null;
  return {
    effectiveType: connection.effectiveType,
    type: connection.type,
    downlink: connection.downlink,
    rtt: connection.rtt,
    saveData: connection.saveData,
  };
}

function describeBuffered(audio: HTMLAudioElement) {
  const ranges: Array<[number, number]> = [];
  for (let index = 0; index < audio.buffered.length && index < 8; index += 1) {
    ranges.push([roundSeconds(audio.buffered.start(index)), roundSeconds(audio.buffered.end(index))]);
  }
  return ranges;
}

export function usePlaybackTelemetry(
  audioRef: RefObject<HTMLAudioElement | null>,
  track: Track,
  audioContextRef?: RefObject<AudioContext | null>,
): PlaybackLogger {
  const sessionIdRef = useRef(getClientSessionId());
  const trackRef = useRef(track);
  const queueRef = useRef<QueuedEvent[]>([]);
  const lastAdvanceRef = useRef({ position: 0, at: 0 });
  const lastStallLogRef = useRef(0);

  useEffect(() => {
    trackRef.current = track;
  }, [track]);

  const flush = useCallback((beacon = false) => {
    const queue = queueRef.current;
    if (queue.length === 0) return;
    const events = queue.splice(0, queue.length);
    const payload = JSON.stringify({
      sessionId: sessionIdRef.current,
      userAgent: navigator.userAgent,
      events,
    });

    if (beacon && typeof navigator.sendBeacon === 'function') {
      const body = new Blob([payload], { type: 'application/json' });
      if (navigator.sendBeacon(logEndpoint, body)) return;
    }

    void fetch(logEndpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: payload,
      cache: 'no-store',
      keepalive: true,
    }).catch(() => undefined);
  }, []);

  const snapshot = useCallback((): PlaybackEventDetail => {
    const audio = audioRef.current;
    const activeTrack = trackRef.current;
    const detail: PlaybackEventDetail = {
      trackId: activeTrack.track_id,
      audioKey: audioKey(activeTrack),
      visibility: document.visibilityState,
    };
    if (audio) {
      const buffered = describeBuffered(audio);
      detail.position = roundSeconds(audio.currentTime);
      detail.duration = roundSeconds(audio.duration);
      detail.paused = audio.paused;
      detail.readyState = audio.readyState;
      detail.networkState = audio.networkState;
      detail.currentSrc = audio.currentSrc;
      detail.buffered = buffered;
      detail.bufferedEnd = buffered.reduce((maximum, [, end]) => (end > maximum ? end : maximum), -1);
      detail.mediaError = audio.error ? { code: audio.error.code, message: audio.error.message } : null;
    }
    detail.audioContextState = audioContextRef?.current?.state ?? null;
    detail.connectionType = describeConnection()?.effectiveType ?? null;
    return detail;
  }, [audioContextRef, audioRef]);

  const logEvent = useCallback<PlaybackLogger>((event, detail = {}) => {
    const queue = queueRef.current;
    queue.push({ event, at: Date.now(), detail: { ...snapshot(), ...detail } });
    if (queue.length > maximumQueuedEvents) {
      queue.splice(0, queue.length - maximumQueuedEvents);
    }
    if (immediateFlushEvents.has(event)) flush();
  }, [flush, snapshot]);

  useEffect(() => {
    const timer = window.setInterval(() => flush(), flushIntervalMs);
    const onPageHide = () => flush(true);
    window.addEventListener('pagehide', onPageHide);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('pagehide', onPageHide);
      flush(true);
    };
  }, [flush]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    logEvent('session', {
      userAgent: navigator.userAgent,
      language: navigator.language,
      platform: navigator.platform,
      hardwareConcurrency: navigator.hardwareConcurrency,
      deviceMemory: (navigator as Navigator & { deviceMemory?: number }).deviceMemory,
      maxTouchPoints: navigator.maxTouchPoints,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      connection: describeConnection(),
      audioBaseUrl: import.meta.env.VITE_AUDIO_BASE_URL ?? '/',
    });

    const listeners = mediaEventNames.map((name) => {
      const listener = () => logEvent(name);
      audio.addEventListener(name, listener);
      return { name, listener };
    });

    const onVisibilityChange = () => logEvent('visibility', { hidden: document.hidden });
    document.addEventListener('visibilitychange', onVisibilityChange);

    lastAdvanceRef.current = { position: audio.currentTime, at: performance.now() };
    const stallTimer = window.setInterval(() => {
      const element = audioRef.current;
      if (!element) return;
      const now = performance.now();
      if (element.currentTime !== lastAdvanceRef.current.position) {
        lastAdvanceRef.current = { position: element.currentTime, at: now };
      }
      const stalledFor = (now - lastAdvanceRef.current.at) / 1000;
      if (element.paused || element.ended || stalledFor < stallTriggerSeconds) return;
      if (now - lastStallLogRef.current < stallLogThrottleMs) return;
      lastStallLogRef.current = now;
      logEvent('stall', {
        stalledFor: roundSeconds(stalledFor),
        readyState: element.readyState,
        networkState: element.networkState,
      });
    }, stallCheckIntervalMs);

    return () => {
      for (const { name, listener } of listeners) audio.removeEventListener(name, listener);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.clearInterval(stallTimer);
    };
  }, [audioRef, logEvent]);

  return logEvent;
}
